import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { logger } from "@/infrastructure/services/LoggerService";
import { mergeFieldsApi } from "../infrastructure/api/merge-fields-api";
import { PREFERENCE_CENTER_MERGE_KEY, WEB_VERSION_MERGE_KEY } from "../domain/mergeFieldTokens";
// Keys exposed elsewhere in the editor and therefore hidden from the merge-tag dropdown to avoid
// duplication: `preference_center_url` and `web_version_url` are offered as Unlayer "special links"
// (see UnlayerEditor.vue) so they can be applied to a button/text; keeping them in the merge-tag
// list too would show them twice.
const HIDDEN_MERGE_TAG_KEYS = new Set([PREFERENCE_CENTER_MERGE_KEY, WEB_VERSION_MERGE_KEY]);
// Module-level cache — the catalogue is static platform config, fetched once per session and
// shared across both editors. The mapping stays reactive to locale via the computed below.
const catalogue = ref([]);
let loadPromise = null;
/** Test-only: clears the module-level cache between unit tests. */
export function __resetMergeFieldsCache() {
    catalogue.value = [];
    loadPromise = null;
}
/**
 * Raw catalogue keys for the content audit (OP-5630), lower-cased so `has` is case-insensitive
 * without re-allocating per call. Deliberately NOT derived from `mergeTags`: that computed drops
 * the keys hidden from the dropdown (see `HIDDEN_MERGE_TAG_KEYS`) and relabels the rest, while the
 * audit must see every resolvable key.
 *
 * CONSTRAINT: module-level, like the loader below, because `useCampaignEditorStore` reads them and
 * cannot call `useMergeFields()` — the composable needs `useI18n()`'s component context and a Pinia
 * store setup has none. Moving either back inside the composable throws on the send screen only,
 * where no unit test currently looks.
 */
export const mergeFieldCatalogueKeys = computed(() => new Set(catalogue.value.map((field) => field.key.toLowerCase())));
/** Loads the catalogue once per session. Safe to call without a component context. */
export async function loadMergeFieldCatalogue() {
    if (loadPromise) {
        return loadPromise;
    }
    loadPromise = mergeFieldsApi
        .list()
        .then((rows) => {
        catalogue.value = rows;
    })
        .catch((err) => {
        // Silent fetch (no toast) — must log (R47). Reset so a later mount can retry.
        logger.error("Failed to load merge-field catalogue", err);
        loadPromise = null;
    });
    return loadPromise;
}
export function useMergeFields() {
    const { t } = useI18n();
    const mergeTags = computed(() => {
        var _a;
        const grouped = {};
        for (const field of catalogue.value) {
            if (HIDDEN_MERGE_TAG_KEYS.has(field.key))
                continue;
            const group = (grouped[_a = field.group] ?? (grouped[_a] = {
                name: t(`templating.mergeTagGroups.${field.group}`),
                mergeTags: {},
            }));
            group.mergeTags[field.key] = { name: t(field.labelKey), value: field.token };
        }
        return grouped;
    });
    return {
        mergeTags,
        catalogueKeys: mergeFieldCatalogueKeys,
        loadMergeFields: loadMergeFieldCatalogue,
    };
}
